import { Glass } from "@coach/ui";
import { useQuery } from "@tanstack/react-query";
import Konva from "konva";
import {
  ChevronDown,
  ChevronLeft,
  Download,
  Film,
  Maximize2,
  Redo2,
  RotateCw,
  Save,
  Settings2,
  Trash2,
  Undo2,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Line as KonvaLine, Layer, Stage } from "react-konva";
import { useSync } from "../../providers/sync-provider.js";
import { listPlayers, type PlayerRow } from "../../repos/players.js";
import { DrawingLayer } from "../canvas-kit/drawing/drawing-layer.jsx";
import type { DrawTool } from "../canvas-kit/drawing/types.js";
import { pointInPolygon, useDrawing } from "../canvas-kit/drawing/use-drawing.js";
import {
  type AssemblyInfo,
  boardPosterAssembly,
  currentFrameAsPages,
  exportRequestToPdf,
  framesAsPages,
  type PageDef,
  POSTER_OVERLAP_MM,
  POSTER_PRINT_SHEET,
  POSTER_PRINT_SHEET_ID,
  POSTER_TRIM_MM,
  posterTilingOnPrintSheet,
  renderRequestToPngs,
  tileGridForBoard,
} from "../canvas-kit/export/index.js";
import { fitViewport, usePinchZoom } from "../canvas-kit/stage/use-pinch-zoom.js";
import {
  ExportDialog,
  type ExportDialogBuildArgs,
  type ExportLayoutPreview,
  type ExportMode,
} from "../canvas-kit/ui/export-dialog.jsx";
import { LabelDialog } from "../canvas-kit/ui/label-dialog.jsx";
import { type PaletteAnchor, ToolPalette } from "../canvas-kit/ui/tool-palette/tool-palette.jsx";
import { UnsavedChangesDialog } from "../canvas-kit/ui/unsaved-changes-dialog.jsx";
import { useExportSheet } from "../export-sheet/index.js";
import { BoardSettingsDialog } from "./board-settings-dialog.jsx";
import { FrameStrip } from "./frame-strip.jsx";
import { Pitch } from "./pitch.jsx";
import { tacticsRenderer } from "./render-snapshot.js";
import { RosterPanel } from "./roster-panel.jsx";
import {
  type BoardSnapshot,
  boardDimensions,
  emptySnapshot,
  FIELD_KINDS,
  type FieldKind,
  type NameDisplay,
  type Token,
  type TokenSizePreset,
} from "./scene.js";
import { TokenContextMenu } from "./token-context-menu.jsx";
import { TokensLayer } from "./tokens-layer.jsx";
import { TACTICS_PALETTE_STYLE_TOOLS, TACTICS_TOOLS, type ToolMode } from "./tools.js";
import { TrailLayer } from "./trail-layer.jsx";
import { computeTrails, usePlayback } from "./use-playback.js";
import { useSceneState } from "./use-scene-state.js";

/**
 * Tactical board editor — Phases 1 – 3.
 *
 * Phase 3 adds:
 *   - Drawing tools: pen, line, arrow, rect, circle  (color + width + dashed)
 *   - Text tool (tap empty → prompt → annotation)
 *   - Lasso multi-select (selects tokens whose center sits inside the loop)
 *   - Unified `selectedIds` set across tokens, strokes, shapes, texts
 *   - Bulk delete via the trash button in the palette
 */

if (typeof window !== "undefined") {
  Konva.hitOnDragEnabled = true;
  // @ts-expect-error captureTouchEventsEnabled exists at runtime, not in types
  Konva.captureTouchEventsEnabled = true;
}

const DRAW_TOOL_SET = new Set<ToolMode>(["pen", "line", "arrow", "rect", "circle", "lasso"]);

/**
 * Sheet sizes offered as one-click chips in the poster view, A2 being the
 * default. A1 is included for large dressing-room boards; A3 because it is
 * far cheaper to print. All DIN A formats share the board's √2 ratio, so each
 * one fills the sheet completely.
 */
const POSTER_PAPER_IDS = ["A4", "A3", "A2", "A1"] as const;

interface EditorProps {
  name: string;
  snapshot: BoardSnapshot;
  saving?: boolean;
  onSave: (next: { name: string; snapshot: BoardSnapshot }) => void;
  onDelete?: () => void;
  onClose: () => void;
}

export function TacticsEditor({ name, snapshot, saving, onSave, onDelete, onClose }: EditorProps) {
  const [draftName, setDraftName] = useState(name);
  // Track name edits separately — `scene.dirty` is derived from snapshot
  // identity only and doesn't know about the title input.
  const [nameDirty, setNameDirty] = useState(false);
  const scene = useSceneState(snapshot);
  const fieldKind = scene.snapshot.fieldKind;
  const orientation = scene.snapshot.orientation;
  const tokenSizePreset: TokenSizePreset = scene.snapshot.tokenSizePreset ?? "normal";
  const nameDisplay: NameDisplay = scene.snapshot.nameDisplay ?? "full";

  // Playback (Phase 4) — drives the rendered scene while playing.
  const playback = usePlayback({
    snapshot: scene.snapshot,
    currentIndex: scene.currentFrameIndex,
    onAdvance: scene.selectFrame,
  });
  const [showTrails, setShowTrails] = useState(true);
  // Frames panel starts hidden — it's an advanced authoring affordance and
  // the empty default scene has nothing meaningful to show in the strip.
  const [frameStripOpen, setFrameStripOpen] = useState(false);
  // Recording ("Aufnahme") lives in the frame strip: it ends as soon as
  // playback starts or the strip is closed, so edits never silently branch
  // frames while the coach can't see the strip.
  const { recording, setRecording } = scene;
  useEffect(() => {
    if (recording && (playback.playing || !frameStripOpen)) setRecording(false);
  }, [recording, playback.playing, frameStripOpen, setRecording]);
  // Tool palette is shown by default; the toggle lets the coach hide it to get
  // an unobstructed view of the pitch.
  const [paletteOpen, setPaletteOpen] = useState(true);

  // Tool-palette dock side. Persisted so the coach gets the same layout
  // next time they open the board. Four anchors: left/right are
  // vertical, top/bottom are horizontal (with horizontal scroll).
  const TOOLBAR_ANCHOR_KEY = "coach.taktikboard.toolbarAnchor.v1";
  const [paletteAnchor, setPaletteAnchor] = useState<PaletteAnchor>(() => {
    if (typeof window === "undefined") return "left";
    const raw = window.localStorage.getItem(TOOLBAR_ANCHOR_KEY);
    if (raw === "left" || raw === "right" || raw === "top" || raw === "bottom") {
      return raw;
    }
    return "left";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(TOOLBAR_ANCHOR_KEY, paletteAnchor);
    } catch {
      /* storage disabled — ignore */
    }
  }, [paletteAnchor]);
  const trails = useMemo(
    // Laufwege are tied to the frames panel — if the user has the frames
    // strip collapsed, the trails would be visual noise without context,
    // so we suppress them regardless of the `showTrails` toggle.
    () => (showTrails && frameStripOpen && !playback.playing ? computeTrails(scene.frames) : []),
    [showTrails, frameStripOpen, playback.playing, scene.frames],
  );
  /** Read-only scene used for rendering — interpolated during playback. */
  const renderScene = playback.playing ? playback.scene : scene.scene;

  const [tool, setTool] = useState<ToolMode>("select");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { openExportSheet, sheet: exportSheet } = useExportSheet();
  const [menu, setMenu] = useState<{
    tokenId: string;
    screenX: number;
    screenY: number;
  } | null>(null);
  /** Active inline dialog for setting a token's label/number (opponents + dummies). */
  const [labelDialog, setLabelDialog] = useState<{
    tokenId: string;
    title: string;
    initialLabel: string;
    initialNumber: string;
  } | null>(null);
  /** Active inline dialog for editing a free-form text node. */
  const [textDialog, setTextDialog] = useState<{
    textId: string | null; // null = new text at pos
    initialValue: string;
    pos?: { x: number; y: number };
  } | null>(null);
  const [style, setStyle] = useState({
    color: "#97fc00",
    width: 6,
    dashed: false,
  });

  // Players
  const { db, orgId, defaultTeamId } = useSync();
  const playersQuery = useQuery({
    queryKey: ["tactics", "players", orgId, defaultTeamId],
    enabled: !!db && !!orgId && !!defaultTeamId,
    refetchInterval: 5000,
    queryFn: async () => listPlayers(db!, orgId!, defaultTeamId!),
  });
  const players: PlayerRow[] = playersQuery.data ?? [];
  const playerById = useMemo(() => {
    const m = new Map<string, PlayerRow>();
    for (const p of players) m.set(p.id, p);
    return m;
  }, [players]);

  useEffect(() => {
    setDraftName(name);
    setNameDirty(false);
    setSelectedIds(new Set());
    setMenu(null);
  }, [name, snapshot]);

  // Keyboard shortcuts: ⌘Z / Ctrl-Z = Undo, ⇧⌘Z / Ctrl-Y = Redo, Delete/Backspace = bulk delete
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isFormField =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && (e.key === "z" || e.key === "Z")) {
        if (isFormField) return;
        e.preventDefault();
        if (e.shiftKey) scene.redo();
        else scene.undo();
        return;
      }
      if (cmd && (e.key === "y" || e.key === "Y")) {
        if (isFormField) return;
        e.preventDefault();
        scene.redo();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && !isFormField && selectedIds.size > 0) {
        e.preventDefault();
        handleDeleteSelected();
        return;
      }
      // Playback shortcuts (Phase 4 polish): Space toggles, ←/→ step frames
      if (e.key === " " && !isFormField && scene.frames.length > 1) {
        e.preventDefault();
        playback.toggle();
        return;
      }
      if (e.key === "ArrowLeft" && !isFormField && !cmd) {
        if (scene.currentFrameIndex > 0) {
          e.preventDefault();
          if (playback.playing) playback.pause();
          scene.selectFrame(scene.currentFrameIndex - 1);
        }
        return;
      }
      if (e.key === "ArrowRight" && !isFormField && !cmd) {
        if (scene.currentFrameIndex < scene.frames.length - 1) {
          e.preventDefault();
          if (playback.playing) playback.pause();
          scene.selectFrame(scene.currentFrameIndex + 1);
        }
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const { width: boardW, height: boardH } = boardDimensions(orientation, fieldKind);

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const initialVp = useMemo(() => {
    if (stageSize.width === 0 || stageSize.height === 0) return { scale: 1, x: 0, y: 0 };
    return fitViewport(boardW, boardH, stageSize.width, stageSize.height);
  }, [boardW, boardH, stageSize.width, stageSize.height]);

  // Filled in below once drawing/stage/pan refs exist — aborts whatever the
  // first finger started when a two-finger pinch/pan gesture begins.
  const gestureCancelRef = useRef<() => void>(() => {});
  const { viewport, setViewport, reset, containerRef, onWheel, isGesturing, gesturing } =
    usePinchZoom({
      initial: initialVp,
      onGestureStart: () => gestureCancelRef.current(),
    });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setStageSize({ width: rect.width, height: rect.height });
    });
    ro.observe(el);
    const rect = el.getBoundingClientRect();
    setStageSize({ width: rect.width, height: rect.height });
    return () => ro.disconnect();
  }, [containerRef]);

  useEffect(() => {
    if (stageSize.width === 0 || stageSize.height === 0) return;
    setViewport(fitViewport(boardW, boardH, stageSize.width, stageSize.height));
  }, [boardW, boardH, stageSize.width, stageSize.height, setViewport]);

  // ── Drawing controller ──────────────────────────────────────
  const drawTool: DrawTool | null = useMemo(() => {
    if (
      tool === "pen" ||
      tool === "line" ||
      tool === "arrow" ||
      tool === "rect" ||
      tool === "circle" ||
      tool === "lasso"
    )
      return tool;
    return null;
  }, [tool]);

  const drawing = useDrawing({
    tool: drawTool,
    style,
    onCommitStroke: (st) => scene.addStroke(st),
    onCommitShape: (sh) => scene.addShape(sh),
    onCommitLasso: (path) => {
      // Hit-test: every element whose anchor sits inside the lasso polygon.
      const hits: string[] = [];
      for (const t of scene.scene.tokens) {
        if (pointInPolygon(t.x, t.y, path)) hits.push(t.id);
      }
      for (const sh of scene.scene.shapes) {
        const cx = sh.kind === "circle" ? sh.x : sh.x + sh.w / 2;
        const cy = sh.kind === "circle" ? sh.y : sh.y + sh.h / 2;
        if (pointInPolygon(cx, cy, path)) hits.push(sh.id);
      }
      for (const tx of scene.scene.texts) {
        if (pointInPolygon(tx.x, tx.y, path)) hits.push(tx.id);
      }
      for (const st of scene.scene.strokes) {
        // any point of the stroke inside the loop counts
        for (let i = 0; i < st.points.length; i += 2) {
          if (pointInPolygon(st.points[i]!, st.points[i + 1]!, path)) {
            hits.push(st.id);
            break;
          }
        }
      }
      setSelectedIds(new Set(hits));
      setTool("select");
    },
  });

  // ── Stage interactions ──────────────────────────────────────
  const stageRef = useRef<Konva.Stage | null>(null);
  /** Active background-pan gesture, captured on empty stage press. */
  const panRef = useRef<{
    active: boolean;
    clientX: number;
    clientY: number;
    startViewport: { scale: number; x: number; y: number };
  } | null>(null);

  const getClientPos = (
    e: Konva.KonvaEventObject<MouseEvent | TouchEvent>,
  ): { x: number; y: number } | null => {
    const evt = e.evt;
    if ("touches" in evt) {
      const t = evt.touches?.[0] ?? evt.changedTouches?.[0];
      return t ? { x: t.clientX, y: t.clientY } : null;
    }
    return { x: (evt as MouseEvent).clientX, y: (evt as MouseEvent).clientY };
  };

  const boardPointer = (stage: Konva.Stage) => {
    const pos = stage.getPointerPosition();
    if (!pos) return null;
    const tr = stage.getAbsoluteTransform().copy().invert();
    return tr.point(pos);
  };

  const handleStageDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      // Two-finger pinch/pan in flight: the 2nd finger's touchstart lands
      // AFTER our pointerdown-based gesture start (pointer events precede
      // touch events) — without this bail-out it would re-arm the background
      // pan and overwrite the pinch's scale on every move.
      if (isGesturing()) return;
      // Drawing tools take precedence
      if (drawTool) {
        drawing.onPointerDown(e);
        return;
      }
      const stage = e.target.getStage();
      if (!stage) return;
      const isEmpty = e.target === stage || e.target.getType() === "Layer";
      if (!isEmpty) return;
      const pt = boardPointer(stage);
      if (!pt) {
        setSelectedIds(new Set());
        return;
      }
      if (tool === "opponent") {
        const n = countByKind(scene.scene.tokens, "opponent") + 1;
        scene.addToken({
          id: crypto.randomUUID(),
          kind: "opponent",
          x: pt.x,
          y: pt.y,
          label: `Gegner ${n}`,
          number: n,
        });
        return;
      }
      if (tool === "dummy") {
        // Dummy = player-kind token without a playerId. Number auto-increments
        // across existing dummies.
        const n =
          scene.scene.tokens.reduce((m, t) => (t.kind === "player" && !t.playerId ? m + 1 : m), 0) +
          1;
        scene.addToken({
          id: crypto.randomUUID(),
          kind: "player",
          x: pt.x,
          y: pt.y,
          label: `Spieler ${n}`,
          number: n,
        });
        return;
      }
      if (tool === "ball") {
        scene.addToken({
          id: crypto.randomUUID(),
          kind: "ball",
          x: pt.x,
          y: pt.y,
        });
        return;
      }
      if (tool === "text") {
        setTextDialog({
          textId: null,
          initialValue: "",
          pos: { x: pt.x, y: pt.y },
        });
        return;
      }
      // Select tool on empty space: clear selection AND start a pan gesture so
      // the user can drag the background to move the field.
      setSelectedIds(new Set());
      if (tool === "select") {
        const cp = getClientPos(e);
        if (cp) {
          panRef.current = {
            active: true,
            clientX: cp.x,
            clientY: cp.y,
            startViewport: { ...viewport },
          };
        }
      }
    },
    [drawTool, drawing, tool, scene, viewport, isGesturing],
  );

  const handleStageMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (isGesturing()) return;
      if (drawTool) {
        drawing.onPointerMove(e);
        return;
      }
      const pan = panRef.current;
      if (pan?.active) {
        const cp = getClientPos(e);
        if (!cp) return;
        const dx = cp.x - pan.clientX;
        const dy = cp.y - pan.clientY;
        setViewport({
          scale: pan.startViewport.scale,
          x: pan.startViewport.x + dx,
          y: pan.startViewport.y + dy,
        });
      }
    },
    [drawTool, drawing, setViewport, isGesturing],
  );

  const handleStageUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (drawTool) drawing.onPointerUp(e);
      if (panRef.current) panRef.current.active = false;
    },
    [drawTool, drawing],
  );

  // When a second finger lands (pinch/pan), abort everything the first finger
  // started: in-flight stroke drafts, the background pan, and any token drag —
  // otherwise the gesture draws lines / drags players as a side effect.
  useEffect(() => {
    gestureCancelRef.current = () => {
      drawing.cancel();
      if (panRef.current) panRef.current.active = false;
      const stage = stageRef.current;
      if (stage) {
        for (const node of stage.find((n: Konva.Node) => n.isDragging())) {
          node.stopDrag();
        }
      }
    };
  }, [drawing.cancel]);

  // ── Roster & token actions ──────────────────────────────────
  const handleAddPlayer = useCallback(
    (p: PlayerRow) => {
      const cx = boardW / 2 + (Math.random() - 0.5) * 120;
      const cy = boardH / 2 + (Math.random() - 0.5) * 120;
      scene.addToken({
        id: crypto.randomUUID(),
        kind: "player",
        x: cx,
        y: cy,
        playerId: p.id,
      });
    },
    [boardW, boardH, scene],
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    const tokenIds: string[] = [];
    for (const t of scene.scene.tokens) if (selectedIds.has(t.id)) tokenIds.push(t.id);
    if (tokenIds.length) scene.removeTokens(tokenIds);
    for (const st of scene.scene.strokes) if (selectedIds.has(st.id)) scene.removeStroke(st.id);
    for (const sh of scene.scene.shapes) if (selectedIds.has(sh.id)) scene.removeShape(sh.id);
    for (const tx of scene.scene.texts) if (selectedIds.has(tx.id)) scene.removeText(tx.id);
    setSelectedIds(new Set());
    setMenu(null);
  }, [selectedIds, scene]);

  const handleSelect = useCallback((id: string | null) => {
    if (id == null) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set([id]));
  }, []);

  const handleRename = useCallback(() => {
    if (!menu) return;
    const token = scene.scene.tokens.find((t) => t.id === menu.tokenId);
    if (!token) return;
    const title =
      token.kind === "opponent"
        ? "Gegner bearbeiten"
        : token.kind === "player"
          ? "Dummy bearbeiten"
          : "Beschriften";
    setLabelDialog({
      tokenId: token.id,
      title,
      initialLabel: token.label ?? "",
      initialNumber: token.number != null ? String(token.number) : "",
    });
    setMenu(null);
  }, [menu, scene.scene.tokens]);

  const handleLabelDialogConfirm = useCallback(
    ({ label, number }: { label: string; number: string }) => {
      if (!labelDialog) return;
      const asNumber = Number.parseInt(number, 10);
      scene.updateToken(labelDialog.tokenId, {
        label: label || null,
        number: Number.isFinite(asNumber) ? asNumber : null,
      });
      setLabelDialog(null);
    },
    [labelDialog, scene],
  );

  const handleTextDblClick = useCallback(
    (id: string) => {
      const tx = scene.scene.texts.find((t) => t.id === id);
      if (!tx) return;
      setTextDialog({ textId: id, initialValue: tx.value });
    },
    [scene],
  );

  const handleTextDialogConfirm = useCallback(
    (value: string) => {
      if (!textDialog) return;
      const trimmed = value.trim();
      if (textDialog.textId) {
        if (!trimmed) {
          scene.removeText(textDialog.textId);
        } else {
          scene.updateText(textDialog.textId, { value: trimmed });
        }
      } else if (trimmed && textDialog.pos) {
        scene.addText({
          id: crypto.randomUUID(),
          x: textDialog.pos.x,
          y: textDialog.pos.y,
          value: trimmed,
          color: style.color,
          fontSize: Math.max(28, style.width * 6),
        });
      }
      setTextDialog(null);
    },
    [textDialog, scene, style.color, style.width],
  );

  // ── Drag-as-group for multi-selected tokens ─────────────────
  // When the user drags one token and the multi-selection contains it,
  // all other selected tokens follow by the same delta. We move OTHER
  // tokens by directly mutating their Konva nodes during the drag (no
  // React state churn) and then commit a single `moveTokens` history
  // entry on drag-end.
  const groupAnchorsRef = useRef<Map<string, { x: number; y: number }> | null>(null);
  const handleTokenDragStart = useCallback(
    (id: string, x: number, y: number) => {
      if (selectedIds.size > 1 && selectedIds.has(id)) {
        const anchors = new Map<string, { x: number; y: number }>();
        for (const t of scene.scene.tokens) {
          if (selectedIds.has(t.id)) anchors.set(t.id, { x: t.x, y: t.y });
        }
        // re-anchor the dragged token at its current drag pos
        anchors.set(id, { x, y });
        groupAnchorsRef.current = anchors;
      } else {
        groupAnchorsRef.current = null;
      }
    },
    [selectedIds, scene.scene.tokens],
  );
  const handleTokenDragMove = useCallback((id: string, x: number, y: number) => {
    const anchors = groupAnchorsRef.current;
    if (!anchors) return;
    const a = anchors.get(id);
    if (!a) return;
    const dx = x - a.x;
    const dy = y - a.y;
    const stage = stageRef.current;
    if (!stage) return;
    for (const [otherId, anchor] of anchors) {
      if (otherId === id) continue;
      const node = stage.findOne(`#tok-${otherId}`);
      if (node) node.position({ x: anchor.x + dx, y: anchor.y + dy });
    }
  }, []);
  const handleTokenDragEnd = useCallback(
    (id: string, x: number, y: number) => {
      const anchors = groupAnchorsRef.current;
      if (anchors && anchors.size > 1) {
        const a = anchors.get(id);
        if (a) {
          const dx = x - a.x;
          const dy = y - a.y;
          const deltas = new Map<string, { x: number; y: number }>();
          for (const [oid, anchor] of anchors) {
            deltas.set(oid, { x: anchor.x + dx, y: anchor.y + dy });
          }
          scene.moveTokens(deltas);
          groupAnchorsRef.current = null;
          return;
        }
      }
      groupAnchorsRef.current = null;
      scene.moveToken(id, x, y);
    },
    [scene],
  );

  const handleSave = () => {
    // Capture a small screenshot of just the pitch area to use as the cover
    // thumbnail in the overview list. We crop the Stage to the current pitch
    // rectangle in screen coords (viewport.x/y + boardW/H * scale), so the
    // resulting image is exactly the field with all tokens, drawings and
    // labels rendered. JPEG keeps the data URL small (a few KB) so we can
    // safely embed it in the snapshot JSON.
    let cover: string | null = null;
    try {
      const stage = stageRef.current;
      if (stage) {
        const wScreen = boardW * viewport.scale;
        const hScreen = boardH * viewport.scale;
        if (wScreen > 4 && hScreen > 4) {
          const THUMB_W = 480;
          const pixelRatio = Math.max(0.4, Math.min(2, THUMB_W / Math.max(wScreen, 1)));
          cover = stage.toDataURL({
            x: viewport.x,
            y: viewport.y,
            width: wScreen,
            height: hScreen,
            pixelRatio,
            mimeType: "image/jpeg",
            quality: 0.72,
          });
        }
      }
    } catch {
      /* canvas tainted or stage unavailable — ship without a cover */
    }

    onSave({
      name: draftName.trim() || "Unbenannter Spielzug",
      snapshot: { ...scene.snapshot, cover },
    });
    // Optimistically clear the dirty marker. If the save later fails the
    // parent can re-feed a snapshot via the `snapshot` prop and our internal
    // replace effect will reset things; in the success path this immediately
    // hides the „Ungespeichert“ pill regardless of whether the parent passes
    // back a freshly parsed (new-reference) snapshot.
    scene.markClean();
    setNameDirty(false);
  };

  /** Confirm-on-exit dialog visibility (P3). */
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  /**
   * Close-intent handler (P3). If the scene is clean, leave immediately;
   * otherwise open the unsaved-changes dialog and let the user choose
   * between "Speichern & Verlassen", "Verwerfen" and "Abbrechen".
   */
  const requestClose = useCallback(() => {
    if (!scene.dirty && !nameDirty) {
      onClose();
      return;
    }
    setConfirmExitOpen(true);
  }, [scene.dirty, nameDirty, onClose]);

  const tokens = renderScene.tokens;
  const menuToken = menu ? (tokens.find((t) => t.id === menu.tokenId) ?? null) : null;
  const cursorClass = drawTool ? "cursor-crosshair" : "";

  // ── Export (aktuelles Feld OR alle Frames) ─────────────────────────────
  // "Aktuelles Feld" comes first and opens by default: printing one pitch as
  // a poster is the common case. Its simple view asks for nothing but the
  // sheet size — the board is DIN-proportioned, so any of these fills the
  // paper edge to edge. Everything else lives behind "Erweiterte Optionen".
  const exportModes = useMemo<ExportMode[]>(
    () => [
      {
        id: "current",
        label: "Aktuelles Feld",
        supportsScale: true,
        simplePaperIds: POSTER_PAPER_IDS,
        simpleDefaultPaperId: "A2",
        printSheetId: POSTER_PRINT_SHEET_ID,
        posterOverlapMm: POSTER_OVERLAP_MM,
      },
      { id: "frames", label: "Alle Frames" },
    ],
    [],
  );
  const buildExportPages = useCallback(
    ({
      paper,
      orientation: pageOrientation,
      modeId,
      scale,
      overlapMm,
      tileOntoPrintSheet,
    }: ExportDialogBuildArgs): PageDef[] => {
      const { width, height } = boardDimensions(pageOrientation, fieldKind);
      const board = { x: 0, y: 0, w: width, h: height };
      if (modeId === "current") {
        const idx = Math.max(
          0,
          Math.min(scene.currentFrameIndex, scene.snapshot.frames.length - 1),
        );
        const frame = scene.snapshot.frames[idx];
        if (!frame) return [];
        // Tiled poster: print onto A4 sheets, sized so the grid stays at its
        // natural minimum, and mark the glue strips for cutting.
        const tiling = tileOntoPrintSheet
          ? posterTilingOnPrintSheet({
              board,
              posterPaper: paper,
              posterOrientation: pageOrientation,
              overlapMm,
            })
          : null;
        if (tiling) {
          return currentFrameAsPages({
            frameIndex: idx,
            frameId: frame.id,
            frameTitle: `Frame ${idx + 1}`,
            board,
            paper: POSTER_PRINT_SHEET,
            orientation: tiling.printOrientation,
            scale: tiling.scale,
            overlapMm,
            sheetMarks: true,
            trimMm: POSTER_TRIM_MM,
          });
        }
        return currentFrameAsPages({
          frameIndex: idx,
          frameId: frame.id,
          frameTitle: `Frame ${idx + 1}`,
          board,
          paper,
          orientation: pageOrientation,
          scale,
          overlapMm,
        });
      }
      return framesAsPages({
        frames: scene.snapshot.frames.map((f, i) => ({
          id: f.id,
          title: `Frame ${i + 1}`,
        })),
        board,
        paper,
        orientation: pageOrientation,
      });
    },
    [scene.snapshot, scene.currentFrameIndex, fieldKind],
  );
  const getExportLayoutPreview = useCallback(
    ({
      paper,
      orientation: pageOrientation,
      modeId,
      scale,
      overlapMm,
    }: ExportDialogBuildArgs): ExportLayoutPreview | null => {
      if (modeId !== "current") return null;
      const { width, height } = boardDimensions(pageOrientation, fieldKind);
      const board = { x: 0, y: 0, w: width, h: height };
      const { cols, rows, pageBoardW, pageBoardH, stepBoardW, stepBoardH } = tileGridForBoard({
        board,
        paper,
        orientation: pageOrientation,
        scale,
        overlapMm,
      });
      return {
        cols,
        rows,
        boardW: width,
        boardH: height,
        pageBoardW,
        pageBoardH,
        stepBoardW,
        stepBoardH,
        overlapMm,
      };
    },
    [fieldKind],
  );
  const getExportAssembly = useCallback(
    ({
      paper,
      orientation: pageOrientation,
      modeId,
      overlapMm,
      tileOntoPrintSheet,
    }: ExportDialogBuildArgs): AssemblyInfo | null => {
      if (modeId !== "current" || !tileOntoPrintSheet) return null;
      const { width, height } = boardDimensions(pageOrientation, fieldKind);
      const tiling = posterTilingOnPrintSheet({
        board: { x: 0, y: 0, w: width, h: height },
        posterPaper: paper,
        posterOrientation: pageOrientation,
        overlapMm,
      });
      if (!tiling) return null;
      return boardPosterAssembly(tiling, paper.id, overlapMm);
    },
    [fieldKind],
  );

  const handleExportConfirm = useCallback(
    async (req: Parameters<typeof exportRequestToPdf>[1]) => {
      const base = (draftName.trim() || "spielzug").replace(/[^a-z0-9-_]+/gi, "-");
      const exportSnap = { snapshot: scene.snapshot, players: playerById };
      if (req.format === "pdf") {
        const bytes = await exportRequestToPdf(exportSnap, req, tacticsRenderer);
        setExportOpen(false);
        await openExportSheet({
          bytes,
          filename: `${req.filename ?? base}.pdf`,
          mime: "application/pdf",
          title: "Spielzug exportieren",
        });
      } else {
        const rendered = await renderRequestToPngs(exportSnap, req, tacticsRenderer);
        setExportOpen(false);
        if (rendered.length === 1 && rendered[0]) {
          // Single page → standard post-export action chooser.
          await openExportSheet({
            bytes: rendered[0].pngBytes,
            filename: `${req.filename ?? base}.png`,
            mime: "image/png",
            title: "Spielzug exportieren",
          });
        } else {
          // Multi-page PNG keeps the legacy "download every page" flow —
          // the ExportSheet currently models a single file at a time.
          const { downloadBytes } = await import("../canvas-kit/export/to-pdf.js");
          rendered.forEach((r, i) => {
            const suffix = rendered.length > 1 ? `-${i + 1}` : "";
            downloadBytes(r.pngBytes, `${req.filename ?? base}${suffix}.png`, "image/png");
          });
        }
      }
      setExportOpen(false);
    },
    [scene.snapshot, playerById, draftName, openExportSheet],
  );

  return (
    <div className="flex h-full flex-col gap-3 px-3 pb-3 pt-3 sm:px-4">
      {/* ── Topbar ─────────────────────────────────────────────── */}
      <section className="glass-floating flex flex-nowrap items-center gap-3 overflow-x-auto rounded-3xl px-4 py-3 scrollbar-thin">
        <button
          type="button"
          onClick={requestClose}
          className="inline-flex h-10 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          aria-label="Zurück zur Übersicht"
        >
          <ChevronLeft className="size-4" />
          Übersicht
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || (!scene.dirty && !nameDirty)}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] transition-shadow hover:shadow-glow disabled:opacity-60"
        >
          <Save className="size-4" />
          {saving ? "Speichere…" : "Speichern"}
        </button>

        <input
          value={draftName}
          onChange={(e) => {
            setDraftName(e.target.value);
            setNameDirty(e.target.value !== name);
          }}
          placeholder="Name des Spielzugs"
          className="h-10 min-w-50 flex-1 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm outline-none focus:border-pitch-lime/60 focus:shadow-glow sm:max-w-90"
        />

        <FieldKindSelect value={fieldKind} onChange={(k) => scene.setMeta({ fieldKind: k })} />

        <button
          type="button"
          onClick={() =>
            scene.setMeta({
              orientation: orientation === "landscape" ? "portrait" : "landscape",
            })
          }
          className="inline-flex h-10 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          title="Format wechseln"
        >
          <RotateCw className="size-4" />
          {orientation === "landscape" ? "Querformat" : "Hochformat"}
        </button>

        <button
          type="button"
          onClick={reset}
          className="inline-flex h-10 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          title="Ansicht zurücksetzen"
        >
          <Maximize2 className="size-4" />
          Anpassen
        </button>

        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="inline-flex h-10 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          title="Exportieren"
        >
          <Download className="size-4" />
          Export
        </button>

        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="inline-flex h-10 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          title="Brett-Einstellungen"
        >
          <Settings2 className="size-4" />
          Einstellungen
        </button>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-white/6 p-1">
            <button
              type="button"
              onClick={scene.undo}
              disabled={!scene.canUndo}
              title="Rückgängig (⌘Z)"
              className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            >
              <Undo2 className="size-4" />
            </button>
            <button
              type="button"
              onClick={scene.redo}
              disabled={!scene.canRedo}
              title="Wiederherstellen (⇧⌘Z)"
              className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            >
              <Redo2 className="size-4" />
            </button>
          </div>
          {selectedIds.size > 0 && (
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {selectedIds.size} ausgewählt
            </span>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex h-10 items-center gap-1 rounded-full bg-[#3a1418] px-3 text-body-sm text-[#ffb1b8] hover:bg-[#4a1c22]"
            >
              <Trash2 className="size-4" />
              Löschen
            </button>
          )}
          <button
            type="button"
            onClick={() => setPaletteOpen((v) => !v)}
            className={`inline-flex size-10 items-center justify-center rounded-full transition-all ${
              paletteOpen
                ? "bg-pitch-lime/15 text-pitch-lime ring-1 ring-pitch-lime/50 shadow-glow"
                : "bg-white/6 text-muted-foreground hover:bg-white/10"
            }`}
            title={paletteOpen ? "Werkzeuge ausblenden" : "Werkzeuge einblenden"}
            aria-pressed={paletteOpen}
            aria-label="Werkzeuge"
          >
            <Wrench className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => setFrameStripOpen((v) => !v)}
            className={`inline-flex size-10 items-center justify-center rounded-full transition-all ${
              frameStripOpen
                ? "bg-pitch-lime/15 text-pitch-lime ring-1 ring-pitch-lime/50 shadow-glow"
                : "bg-white/6 text-muted-foreground hover:bg-white/10"
            }`}
            title={frameStripOpen ? "Frame-Leiste ausblenden" : "Frame-Leiste einblenden"}
            aria-pressed={frameStripOpen}
            aria-label="Frames"
          >
            <Film className="size-4" />
          </button>
        </div>
      </section>

      {/* ── Stage ──────────────────────────────────────────────── */}
      <Glass level="floating" padding="none" className="flex-1 min-h-0 overflow-hidden">
        <div
          ref={containerRef}
          onWheel={onWheel}
          className={`relative h-full w-full touch-none select-none ${cursorClass}`}
          style={{ minHeight: 480 }}
        >
          {(scene.dirty || recording) && (
            <div className="pointer-events-none absolute right-3 top-3 z-30 flex flex-col items-end gap-1.5">
              {scene.dirty && (
                <div className="rounded-full bg-black/45 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-pitch-lime backdrop-blur-md">
                  ● Ungespeichert
                </div>
              )}
              {recording && (
                <div className="rounded-full bg-black/45 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-rose-300 backdrop-blur-md">
                  <span className="motion-safe:animate-pulse">●</span> Aufnahme
                </div>
              )}
            </div>
          )}
          {stageSize.width > 0 && stageSize.height > 0 && (
            <Stage
              ref={(node) => {
                stageRef.current = node;
              }}
              width={stageSize.width}
              height={stageSize.height}
              scaleX={viewport.scale}
              scaleY={viewport.scale}
              x={viewport.x}
              y={viewport.y}
              // During a two-finger gesture the canvas is navigation-only:
              // no hit-testing, so tokens/shapes can't grab the 2nd finger.
              listening={!gesturing}
              onMouseDown={handleStageDown}
              onTouchStart={handleStageDown}
              onMouseMove={handleStageMove}
              onTouchMove={handleStageMove}
              onMouseUp={handleStageUp}
              onTouchEnd={handleStageUp}
            >
              <Layer listening={false}>
                <Pitch kind={fieldKind} orientation={orientation} />
              </Layer>
              <Layer listening={!DRAW_TOOL_SET.has(tool)}>
                <DrawingLayer
                  strokes={renderScene.strokes}
                  shapes={renderScene.shapes}
                  texts={renderScene.texts}
                  draft={drawing.draft}
                  selectedIds={selectedIds}
                  editable={tool === "select" && !playback.playing}
                  onSelectElement={(id) => handleSelect(id)}
                  onMoveShape={(id, x, y) => scene.updateShape(id, { x, y })}
                  onMoveShapeLive={(id, x, y) => scene.updateShapeLive(id, { x, y })}
                  onResizeShape={(id, patch) => scene.updateShape(id, patch)}
                  onResizeShapeLive={(id, patch) => scene.updateShapeLive(id, patch)}
                  onMoveText={(id, x, y) => scene.updateText(id, { x, y })}
                  onResizeText={(id, patch) => scene.updateText(id, patch)}
                  onTextDblClick={handleTextDblClick}
                />
              </Layer>
              {trails.length > 0 && (
                <Layer listening={false}>
                  <TrailLayer trails={trails} />
                </Layer>
              )}
              <Layer listening={!DRAW_TOOL_SET.has(tool) && !playback.playing}>
                <TokensLayer
                  tokens={tokens}
                  players={playerById}
                  fieldKind={fieldKind}
                  sizePreset={tokenSizePreset}
                  nameDisplay={nameDisplay}
                  selectedIds={selectedIds}
                  transformEnabled={tool === "select" && !playback.playing}
                  onSelect={handleSelect}
                  onDragStart={handleTokenDragStart}
                  onDragMove={handleTokenDragMove}
                  onDragEnd={handleTokenDragEnd}
                  onLongPress={(tokenId, screenX, screenY) =>
                    setMenu({ tokenId, screenX, screenY })
                  }
                  onResizeEnd={(patches) => scene.updateTokens(patches)}
                />
              </Layer>
              {/* Lasso preview */}
              {drawing.lassoPath && drawing.lassoPath.length >= 4 && (
                <Layer listening={false}>
                  <KonvaLine
                    points={drawing.lassoPath}
                    stroke="#97fc00"
                    strokeWidth={2.5}
                    dash={[10, 6]}
                    closed
                    fill="rgba(151,252,0,0.10)"
                    lineCap="round"
                    lineJoin="round"
                  />
                </Layer>
              )}
            </Stage>
          )}

          {paletteOpen && (
            <ToolPalette
              tools={TACTICS_TOOLS}
              drawToolIds={TACTICS_PALETTE_STYLE_TOOLS}
              mode={tool}
              onMode={(m) => {
                if (playback.playing) playback.pause();
                setTool(m);
                if (m !== "select" && m !== "lasso") setSelectedIds(new Set());
              }}
              hasSelection={selectedIds.size > 0}
              onDeleteSelection={handleDeleteSelected}
              style={style}
              onStyle={(patch) => setStyle((s) => ({ ...s, ...patch }))}
              anchor={paletteAnchor}
              onAnchor={setPaletteAnchor}
            />
          )}

          <RosterPanel
            players={players}
            isOnPitch={scene.hasPlayerToken}
            onAdd={handleAddPlayer}
            open={tool === "player"}
            onClose={() => setTool("select")}
          />

          <div
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-4 py-1.5 text-center text-[11px] uppercase tracking-widest text-white/70"
            style={{
              // Lift the hint above the tool palette when it is docked at
              // the bottom so it doesn't get covered by the toolbar.
              bottom: paletteAnchor === "bottom" ? "6rem" : "0.75rem",
            }}
          >
            {playback.playing
              ? `Playback · Frame ${scene.currentFrameIndex + 1} → ${scene.currentFrameIndex + 2}`
              : HINT[tool]}
          </div>
        </div>
      </Glass>

      {/* ── Frame strip ───────────────────────────────────────── */}
      {frameStripOpen && (
        <FrameStrip
          frames={scene.frames}
          currentIndex={scene.currentFrameIndex}
          playing={playback.playing}
          progress={playback.progress}
          onSelect={(i) => {
            if (playback.playing) playback.pause();
            scene.selectFrame(i);
          }}
          onAdd={() => scene.addFrame()}
          onDuplicate={(i) => scene.duplicateFrame(i)}
          onRemove={(i) => scene.removeFrame(i)}
          onSetDuration={(i, ms) => scene.setFrameDuration(i, ms)}
          onTogglePlay={playback.toggle}
          onStop={() => {
            playback.stop();
            scene.selectFrame(0);
          }}
          recording={recording}
          onToggleRecording={() => setRecording(!recording)}
          showTrails={showTrails}
          onToggleTrails={setShowTrails}
        />
      )}

      {menu && menuToken && (
        <TokenContextMenu
          token={menuToken}
          screenX={menu.screenX}
          screenY={menu.screenY}
          onRename={handleRename}
          onDelete={() => {
            scene.removeToken(menu.tokenId);
            setMenu(null);
            setSelectedIds(new Set());
          }}
          onClose={() => setMenu(null)}
        />
      )}

      {labelDialog && (
        <LabelDialog
          title={labelDialog.title}
          initialLabel={labelDialog.initialLabel}
          initialNumber={labelDialog.initialNumber}
          onConfirm={handleLabelDialogConfirm}
          onClose={() => setLabelDialog(null)}
        />
      )}

      {textDialog && (
        <LabelDialog
          title={textDialog.textId ? "Text bearbeiten" : "Text hinzufügen"}
          initialLabel={textDialog.initialValue}
          initialNumber=""
          hideNumber
          multiline
          labelFieldTitle="Text"
          onConfirm={({ label }) => handleTextDialogConfirm(label)}
          onClose={() => setTextDialog(null)}
        />
      )}

      {exportOpen && (
        <ExportDialog
          title="Spielzug exportieren"
          modes={exportModes}
          defaultModeId="current"
          buildPages={buildExportPages}
          getLayoutPreview={getExportLayoutPreview}
          getAssembly={getExportAssembly}
          defaultFilename={(draftName.trim() || "spielzug").replace(/[^a-z0-9-_]+/gi, "-")}
          defaultOrientation={orientation}
          onConfirm={handleExportConfirm}
          onClose={() => setExportOpen(false)}
        />
      )}

      {exportSheet}

      {settingsOpen && (
        <BoardSettingsDialog
          tokenSizePreset={tokenSizePreset}
          nameDisplay={nameDisplay}
          onConfirm={({ tokenSizePreset: nextSize, nameDisplay: nextName }) => {
            const patch: Partial<Pick<BoardSnapshot, "tokenSizePreset" | "nameDisplay">> = {};
            if (nextSize !== tokenSizePreset) patch.tokenSizePreset = nextSize;
            if (nextName !== nameDisplay) patch.nameDisplay = nextName;
            if (Object.keys(patch).length > 0) scene.setMeta(patch);
            setSettingsOpen(false);
          }}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      <UnsavedChangesDialog
        open={confirmExitOpen}
        saving={saving}
        onSaveAndClose={() => {
          handleSave();
          setConfirmExitOpen(false);
          onClose();
        }}
        onDiscard={() => {
          setConfirmExitOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmExitOpen(false)}
      />
    </div>
  );
}

function countByKind(tokens: Token[], kind: Token["kind"]): number {
  return tokens.reduce((n, t) => (t.kind === kind ? n + 1 : n), 0);
}

const HINT: Record<ToolMode, string> = {
  select: "Tippen: Auswählen · Halten: Menü · Fläche ziehen: Verschieben · 2 Finger: Zoom",
  lasso: "Mit dem Finger eine Schlaufe ziehen → alles darin auswählen",
  player: "Spieler aus dem Kader rechts antippen",
  dummy: "Auf das Feld tippen → Dummy-Spieler platzieren",
  opponent: "Auf das Feld tippen → Gegner platzieren",
  ball: "Auf das Feld tippen → Ball platzieren",
  pen: "Frei zeichnen · Farbe & Breite rechts · 2 Finger: Zoom & Verschieben",
  line: "Linie ziehen",
  arrow: "Pfeil ziehen — perfekt für Laufwege",
  rect: "Rechteck aufziehen",
  circle: "Kreis aufziehen",
  text: "Tippen → Text eingeben (Doppeltippen bearbeitet)",
};

const FIELD_KIND_LABEL: Record<FieldKind, string> = {
  pitch_green: "Rasen",
  pitch_blank: "Weiß",
  pitch_zones: "Zonen",
  pitch_half: "Halbfeld",
};

/**
 * Lightweight custom select for the field-kind picker. Uses our own
 * pill styling instead of the native `<select>` chrome so it blends with
 * the rest of the topbar.
 */
function FieldKindSelect({
  value,
  onChange,
}: {
  value: FieldKind;
  onChange: (k: FieldKind) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Reposition menu under the trigger (uses fixed positioning so the menu
  // escapes the topbar's `overflow-x-auto` clip).
  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({
      left: r.left,
      top: r.bottom + 6,
      width: Math.max(r.width, 160),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    const handleDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const handleScroll = () => reposition();
    document.addEventListener("mousedown", handleDown);
    window.addEventListener("resize", handleScroll);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      window.removeEventListener("resize", handleScroll);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, reposition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex h-10 items-center gap-1.5 rounded-full border border-white/15 bg-white/4 px-3 text-body-sm hover:bg-white/10"
      >
        {FIELD_KIND_LABEL[value]}
        <ChevronDown className={`size-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{
              position: "fixed",
              left: menuPos.left,
              top: menuPos.top,
              minWidth: menuPos.width,
            }}
            className="z-100 rounded-2xl border border-white/10 bg-[#0d1115]/95 p-1 shadow-xl backdrop-blur-md"
          >
            {FIELD_KINDS.map((k) => {
              const active = value === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(k);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center rounded-xl px-3 py-2 text-left text-body-sm transition-colors ${
                    active ? "bg-pitch-lime/15 text-pitch-lime" : "text-white hover:bg-white/10"
                  }`}
                >
                  {FIELD_KIND_LABEL[k]}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

export function newBoardSnapshot(): BoardSnapshot {
  return emptySnapshot("landscape");
}
